import { useState, type VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';

import { plural } from '../../../../../../../../shared/helpers/plural';
import { useAddToast } from '../../../../../../../contexts/ToastContext';
import useAddMembersToTeamGroup from '../../../../../../../hooks/api/teams/teamGroups/useAddMembersToTeamGroup';
import { fetchAddableMembers, getAddableMembersQueryKey } from '../../../../../../../hooks/api/teams/useAddableMembers';
import queryClient from '../../../../../../../ReactQuery/client';
import { getErrorMessage } from '../../../../../../../ReactQuery/errors';
import Button from '../../../../../../generic/Button/Button';
import ButtonGroup from '../../../../../../generic/Button/ButtonGroup';
import LoaderButton from '../../../../../../generic/Button/LoaderButton';
import FormGroup from '../../../../../../generic/Forms/FormGroup';
import FormLabel from '../../../../../../generic/Forms/FormLabel';
import LabeledSelect from '../../../../../../generic/Forms/LabeledSelect';
import AsyncPaginatedSelect from '../../../../../../generic/Select/AsyncPaginatedSelect';
import useSelectRoles from '../../../hooks/useSelectRoles';
import { type AddableUser, AddUserOption } from '../../../management/AddUserOption';

type Props = {
  isOpen: boolean;
  onHide: () => void;
  teamGroupId: number;
  teamGroupName: string;
};

/**
 * Modal for adding members to a team group.
 *
 * @param {Props} props - The component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.teamGroupName - The name of the team group
 * @param {() => void} props.onHide - Callback when the modal is closed
 */
const AddTeamGroupMemberModal: VoidFunctionComponent<Props> = ({
  isOpen,
  onHide,
  teamGroupId,
  teamGroupName,
}) => {
  const addToast = useAddToast();
  const [selectedUsers, setSelectedUsers] = useState<AddableUser[]>([]);

  const loadPage = async (searchString: string, _loaded: unknown, additional: { page: number } | undefined) => {
    const res = await queryClient.ensureQueryData({
      queryKey: getAddableMembersQueryKey(teamGroupId, 20, searchString),
      queryFn: () => fetchAddableMembers({ pageParam: additional?.page ?? 0 }, teamGroupId, 20, searchString),
    });

    return {
      options:
        res.data?.results.map((user) => ({
          label: user.username || user.email || `user id: ${user.id}`,
          value: user.id,
          isServiceAccount: user.isServiceAccount,
        })) ?? [],
      hasMore: (res.data?.totalCount ?? 0) > (res.data?.page ?? 0) * (res.data?.pageSize ?? 0),
      additional: {
        page: (additional?.page ?? 0) + 1,
      },
    };
  };

  const {
    roleOptions,
    onChangeRole,
    selectedRole,
    isLoading: isLoadingRoles,
  } = useSelectRoles({ teamId: teamGroupId });

  const { mutateAsync: addMembersToTeamGroup, isLoading: isSubmitting } = useAddMembersToTeamGroup(teamGroupId, {
    action: 'add',
    users: selectedUsers.map(user => ({ id: user.value, roleId: selectedRole?.value })),
  });

  const onAdd = async () => {
    try {
      await addMembersToTeamGroup();
      addToast({
        type: 'success',
        content: `Added ${selectedUsers.length} ${plural(selectedUsers.length, 'user')} to "${teamGroupName}"`,
      });
      onHide();
    } catch (error) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, `Failed to add ${plural(selectedUsers.length, 'user')} to "${teamGroupName}"`),
      });
    }
  };

  const isAddButtonDisabled = !selectedUsers.length || !selectedRole || isSubmitting;

  return (
    <Modal
      show={isOpen}
      onHide={onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title className="margin-bottom-sm">
          Add Members to &ldquo;{teamGroupName}&rdquo;
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup>
          <FormLabel label="Select Users">
            <AsyncPaginatedSelect
              id="add-team-group-member-modal-user-select"
              isMulti
              onChange={setSelectedUsers}
              loadOptions={loadPage}
              value={selectedUsers}
              additional={{ page: 1 }}
              debounceTimeout={200}
              components={{
                Option: AddUserOption,
              }}
            />
          </FormLabel>
          <LabeledSelect
            id="add-team-group-member-modal-default-role-select"
            label="Role (for added users)"
            placeholder="Select a role"
            options={roleOptions}
            onChange={onChangeRole}
            isDisabled={isLoadingRoles}
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={onAdd}
            isDisabled={isAddButtonDisabled}
            isLoading={isSubmitting}
          >
            Add
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default AddTeamGroupMemberModal;
